/* eslint-disable no-redeclare */
import type { PropsWithChildren, ReactElement } from 'react';

import type { OneOrMany } from '@utilities/helpers/OneOrMany';

import type { ButtonProps, StandardButtonProps } from '../../Button';
import { Button } from '../../Button';
import { SocialIconDebank } from './icons/SocialIconDebank';
import { SocialIconDiscord } from './icons/SocialIconDiscord';
import { SocialIconFacebook } from './icons/SocialIconFacebook';
import { SocialIconLinkedin } from './icons/SocialIconLinkedin';
import { SocialIconMail } from './icons/SocialIconMail';
import { SocialIconMedium } from './icons/SocialIconMedium';
import { SocialIconReddit } from './icons/SocialIconReddit';
import { SocialIconTelegram } from './icons/SocialIconTelegram';
import { SocialIconWeb } from './icons/SocialIconWeb';
import { SocialIconWhatsapp } from './icons/SocialIconWhatsapp';
import { SocialIconX } from './icons/SocialIconX';
import { SocialIconYoutube } from './icons/SocialIconYoutube';
import styles from './SocialMedia.module.scss';

const SOCIAL_MEDIA = [
  'CUSTOM',
  'X',
  'DISCORD',
  'TELEGRAM',
  'YOUTUBE',
  'LINKEDIN',
  'REDDIT',
  'DEBANK',
  'MAIL',
  'FACEBOOK',
  'WHATSAPP',
  'MEDIUM',
] as const;

type SocialMedium = (typeof SOCIAL_MEDIA)[number];

const SOCIAL_MEDIUM_ALIASES_MAPPING: Partial<Record<SocialMedium, Array<string>>> = {
  X: ['TWITTER'],
} as const;

const SOCIAL_MEDIUM_ALIASES_LOOKUP_MAPPING: Record<string, SocialMedium> = {};

let medium: SocialMedium;

for (medium in SOCIAL_MEDIUM_ALIASES_MAPPING) {
  //  Reassigning medium to a local variable is necessary to prevent potential error
  //  when an external variable is used inside a loop.
  // See https://eslint.org/docs/latest/rules/no-loop-func
  const _medium = medium;

  SOCIAL_MEDIUM_ALIASES_MAPPING[medium]?.forEach((alias) => {
    SOCIAL_MEDIUM_ALIASES_LOOKUP_MAPPING[alias] = _medium;
  });
}

const SocialMedium = {
  parse: (value: string) => {
    const normalizedValue = value.toUpperCase();

    return (
      SOCIAL_MEDIUM_ALIASES_LOOKUP_MAPPING[normalizedValue] ??
      SOCIAL_MEDIA.find((medium) => medium === value) ??
      'CUSTOM'
    );
  },
} as const;

interface SocialMediaMetadata {
  name: string;
  icon: ReactElement<unknown, 'svg'>;
}

const SOCIAL_MEDIA_METADATA: Record<SocialMedium, SocialMediaMetadata> = {
  CUSTOM: {
    name: 'Web link',
    icon: <SocialIconWeb />,
  },
  X: {
    name: 'X (ex.Twitter)',
    icon: <SocialIconX />,
  },
  DISCORD: {
    name: 'Discord',
    icon: <SocialIconDiscord />,
  },
  TELEGRAM: {
    name: 'Telegram',
    icon: <SocialIconTelegram />,
  },
  YOUTUBE: {
    name: 'YouTube',
    icon: <SocialIconYoutube />,
  },
  LINKEDIN: {
    name: 'LinkedIn',
    icon: <SocialIconLinkedin />,
  },
  REDDIT: {
    name: 'Reddit',
    icon: <SocialIconReddit />,
  },
  DEBANK: {
    name: 'Debank',
    icon: <SocialIconDebank />,
  },
  MAIL: {
    name: 'Mail',
    icon: <SocialIconMail />,
  },
  FACEBOOK: {
    name: 'Facebook',
    icon: <SocialIconFacebook />,
  },
  WHATSAPP: {
    name: 'WhatsApp',
    icon: <SocialIconWhatsapp />,
  },
  MEDIUM: {
    name: 'Medium',
    icon: <SocialIconMedium />,
  },
};

interface SocialMediaLinkProps {
  medium?: SocialMedium;
  url: string;
}

const SocialMediaLink = ({ medium, url }: SocialMediaLinkProps) => {
  return false;
};

interface SocialMediaProps extends PropsWithChildren {
  buttonSize?: ButtonProps['size'];
  buttonVariant?: StandardButtonProps['variant'];
  children?: OneOrMany<ReactElement<SocialMediaLinkProps, typeof SocialMediaLink>>;
}

const SocialMedia = ({
  buttonSize = 'medium',
  buttonVariant = 'primary',
  children = [],
}: SocialMediaProps) => {
  const elements = Array.isArray(children) ? children : [children];

  return (
    <div className={styles['container']} data-icon-size={buttonSize}>
      {elements?.map(({ props: {medium = 'CUSTOM', url } }, index) => (
        <Button
          size={buttonSize}
          variant={buttonVariant}
          href={url}
          target="_blank"
          key={index}
          aria-label={SOCIAL_MEDIA_METADATA[medium].name}
          label={SOCIAL_MEDIA_METADATA[medium].name}
        >
          {SOCIAL_MEDIA_METADATA[medium].icon}
        </Button>
      ))}
    </div>
  );
};

SocialMediaLink.displayName = 'SocialMedia.Link';

SocialMedia.Link = SocialMediaLink;

export { SocialMedia, SocialMedium, SOCIAL_MEDIA_METADATA };
